import React, { useEffect, useMemo } from "react";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import Breadcrumbs from "../components/Breadcrumbs";
import BrandsCard from "../components/BrandsCard";
import brandsData from "../data/brands.json";
/*import GalleryMini from "../components/GalleryMini";*/
import { useQuery } from "@tanstack/react-query";
import { fetchCollections } from "../lib/medusa";
import { useLocation } from "react-router-dom";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";

const BRAND_DISPLAY_ORDER = [
  "parfums-de-marly",
  "fragrance-du-bois",
  "initio",
  "nasomatto",
  "amouage",
  "memo",
  "marc-antoine-barrois",
  "le-labo",
  "molton-brown",
  "clive-christian",
  "orto-parisi",
  "profumum-roma",
  "fueguia",
  "puredistance",
  "eight-and-bob",
  "thameen",
  "jovoy",
  "electimuss",
  "histoires-de-parfums",
  "jeroboam",
  "maison-crivelli",
  "ormonde-jayne",
  "carner-barcelona",
  "liquides-imaginaires",
  "morph",
  "dusita",
  "lorenzo-pazzaglia",
  "venti4",
  "francesca-bianchi",
  "bdk-parfums",
  "rosendo-mateu",
  "olibanum",
  "hugh-parsons",
  "giardini-di-toscana",
  "dr-vranjes",
  "affinessence",
  "mind-games",
  "andy-tauer",
  "kilian-paris",
  "jo-malone",
  "frederic-malle",
  "nicolai",
  "essential-parfums",
  "roja-parfums",
  "escentric-molecules",
  "leif",
  "parfums-mdci",
  "hormone-paris",
  "house-of-brandt",
  "testament",
  "vilhelm-parfumerie",
  "the-naxos-apothecary",
  "eveilleur",
  "bibbi",
  "tom-ford",
];

const brandDisplayRank = new Map(
  BRAND_DISPLAY_ORDER.map((handle, index) => [handle, index]),
);

const sortBrandsByDisplayOrder = (brands) =>
  [...brands].sort((a, b) => {
    const aRank = brandDisplayRank.get(a?.handle) ?? Number.MAX_SAFE_INTEGER;
    const bRank = brandDisplayRank.get(b?.handle) ?? Number.MAX_SAFE_INTEGER;

    if (aRank !== bRank) return aRank - bRank;

    return (a?.title || a?.handle || "").localeCompare(
      b?.title || b?.handle || "",
    );
  });

export default function Brands({ setHeader }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = `${t("header.brands.pageTitle")} - ${SITE_NAME}`;
  const pageDescription = t("header.brands.metaDescription");
  const enUrl = `${SITE_URL}/en/brands`;
  const meUrl = `${SITE_URL}/me/brendovi`;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    { name: t("header.brands.pageTitle"), item: pageUrl },
  ];
  const { data: collections } = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });
  const combinedBrands = useMemo(() => {
    const collectionBrands = collections ?? [];
    const collectionsByHandle = new Map(
      collectionBrands
        .filter((brand) => brand?.handle)
        .map((brand) => [brand.handle, brand]),
    );

    const localBrands = Object.entries(brandsData).filter(([, brand]) => !brand.hidden).map(([handle, brand]) => {
      const collectionBrand = collectionsByHandle.get(handle);

      return {
        ...collectionBrand,
        handle,
        title: brand?.title || collectionBrand?.title || handle,
        metadata: {
          ...(collectionBrand?.metadata || {}),
          logo: brand?.logo || collectionBrand?.metadata?.logo,
        },
      };
    });

    const medusaOnlyBrands = collectionBrands.filter(
      (brand) => brand?.handle && !brandsData[brand.handle],
    );

    return sortBrandsByDisplayOrder([...localBrands, ...medusaOnlyBrands]);
  }, [collections]);


  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={meUrl} hrefLang="sr-ME" />
        <link rel="alternate" href={meUrl} hrefLang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <Loader />
      {/* // <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div className="rs-breadcrumbs img2">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {t("home.brands.title")}
                {/*<span className="watermark">{home.brands.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- Brands Section Start --> */}
        <div
          id="rs-our-sponsor"
          className="rs-our-sponsor lion-bg style1 home3-sponsor-style height-ctrl pt-130 md-pb-70 md-pt-70"
        >
          <div className="container">
            <div className="sec-title text-center mb-60">
              <h2 className="title title2 title3 text-middle">
                {t("home.brands.title")}
              </h2>
            </div>
            <div className="row">
              {combinedBrands &&
                combinedBrands
                  .filter(
                    (brand) =>
                      brand?.metadata?.logo || brandsData[brand?.handle],
                  )
                  .map((brand, i) => <BrandsCard key={i} brand={brand} />)}
            </div>
          </div>
        </div>
        {/* <!-- Brands Section End -->  */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--65">
          <Newsletter />
        </div>

        {/* <!-- Newsletter section end --> */}
      </div>
      {/* // <!-- Main content End --> */}
      <StructuredData data={breadcrumbJsonLd(breadcrumbItems)} />
    </>
  );
}
